import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { AllergyCodeOption } from '@/types/patient-chart-api';

/**
 * Master data — Allergy code typeahead search for the Add Allergy form
 * (PROD-827 chart write surfaces). Used by the non-drug allergen picker.
 *
 * Wire (Flutter `common_data_source_impl.dart:551-570`):
 *
 *   GET /api/Master/AllergyCode?searchQuery={query}
 *       → CodeCategoryData[] (≡ AllergyCodeOption[] React-side)
 *
 * Each row is a CodeWordCase EXTENDED with an optional nested `category`
 * Coding. When the user picks a row, its Coding goes to the CreateAllergy
 * body's `code` and its `category` goes to the body's TOP-LEVEL
 * `category` — the backend ignores a category nested under `code`
 * (PROD-1998).
 *
 * Non-drug allergens only. Drug allergens are searched in the org's
 * medication catalogue (`useMedicationValueSetSearch`,
 * `SearchValueSets2?SearchFor=Medications`), like Flutter's Drug toggle.
 *
 * Gated on a minimum query length to avoid firing on every keystroke
 * AND on `enabled` (the dialog passes `false` when the search box is
 * empty, so we don't fire a request just because the picker opens).
 *
 * **Stale-request safety** (Samer PR #214 review 2026-05-19):
 * - `signal` IS forwarded to `api-client.get()` from the queryFn arg.
 *   React Query auto-cancels in-flight requests when the query key
 *   changes — so backspacing from "ab" → "a" aborts the "ab" request
 *   via AbortController.
 * - When the user drops below `MIN_QUERY_LENGTH`, `enabled` flips
 *   false and no new request is fired.
 * - The dialog's result-panel UI gate ALSO checks
 *   `debounced.trim().length >= 2` before rendering, so even if a
 *   slow request somehow lands (rare), the results aren't shown.
 * - `placeholderData: (prev) => prev` only affects which `data` value
 *   is returned during refetches — it doesn't bypass the query-key
 *   cancellation behavior.
 */

const MIN_QUERY_LENGTH = 2;

export function useAllergyCodeSearch(
  query: string,
  enabled = true,
) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['master-allergy-code', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<AllergyCodeOption[]>(
        `/api/Master/AllergyCode?${qs.toString()}`,
        { signal },
      );
    },
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60 * 1000, // 5 min — search results can be cached briefly
    placeholderData: (prev) => prev, // keep prior results while typing
  });
}
